import React, { FC, useState, useRef, useEffect } from 'react';
import { iconSize } from '../../../../../../constants/icon.constants';
import Icon from '../../../../../@shared/icon';
import { documentService } from '../../../../../../utils/window.utils';
import HiglightedOption from '../../../../../@shared/highlightedOption';
import { TCountry, TDropDownItem, TState } from '../../../../types/dealerLocatorLanding.types';
import { isStateType } from '../../../../dealerLocator.utils';

enum arrowIcons {
  up = 'arrow-chevron-up',
  down = 'arrow-chevron-down',
}

const Dropdown: FC<{
  items: Array<TCountry | TState>;
  preSelectedItem?: TDropDownItem;
  label?: string;
  onSelect: (country: TCountry | TState) => void;
}> = ({ items, preSelectedItem, label, onSelect }) => {
  const [selectedItem, setSelectedItem] = useState<TDropDownItem>(preSelectedItem ?? null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const defaultSearchText = (() => {
    if (!preSelectedItem) {
      return '';
    }

    if (isStateType(preSelectedItem)) {
      return preSelectedItem.name ?? '';
    } else {
      return preSelectedItem?.Country ?? '';
    }
  })();
  const [searchText, setSearchText] = useState<string>(defaultSearchText);
  const [searchTextChanged, setSearchTextChanged] = useState<boolean>(false);
  const [highligtedItemIndex, setHighligtedItemIndex] = useState<number | null>(null);
  const [dropdownHover, setDropdownHover] = useState<boolean>(false);
  const arrowIcon = dropdownOpen ? arrowIcons.up : arrowIcons.down;
  const searchInputElmRef = useRef<HTMLInputElement>(null);
  const dropdownElmRef = useRef<HTMLDivElement>(null);
  const filteredItems = items?.filter((item) => {
    if (isStateType(item)) {
      return item.name.toLocaleLowerCase().includes(searchText?.toLocaleLowerCase());
    }

    return item.Country.toLocaleLowerCase().includes(searchText?.toLocaleLowerCase());
  });
  const visibleItems = searchTextChanged && searchText !== '' ? filteredItems : items;

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSearchTextChanged(false);
    setHighligtedItemIndex(null);
  };

  const handleListItemSelection = (item: TCountry | TState) => {
    setSelectedItem(item);
    setSearchText(isStateType(item) ? item.name : item.Country);
    onSelect(item);
    closeDropdown();
  };

  const handleInputClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e?.target?.value);
    setSearchTextChanged(true);

    if (!dropdownHover) {
      setHighligtedItemIndex(null);
    }
  };

  const handleClickOutside = (e) => {
    if (dropdownElmRef?.current?.contains(e?.target)) {
      return;
    }

    closeDropdown();
  };

  const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && visibleItems.length > 0) {
      if (searchText !== '' && highligtedItemIndex === null) {
        handleListItemSelection(visibleItems[0]);
      } else if (Number.isInteger(highligtedItemIndex)) {
        handleListItemSelection(visibleItems[highligtedItemIndex as number]);
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const index = highligtedItemIndex !== null && highligtedItemIndex < visibleItems.length - 1 ? highligtedItemIndex + 1 : 0;
      setHighligtedItemIndex(index);
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const index = highligtedItemIndex !== null && highligtedItemIndex > 0 ? highligtedItemIndex - 1 : visibleItems.length - 1;
      setHighligtedItemIndex(index);
    }
  };

  const getOptionText: (item: TDropDownItem) => string = (item) => {
    if (!item) {
      return '';
    }

    if (isStateType(item)) {
      return item.name;
    }

    return item.Country;
  };

  const getOptionKey: (item: TCountry | TState) => string = (item) => {
    if (isStateType(item)) {
      return item.name.replace('', '-');
    }

    return item.Country;
  };

  useEffect(() => {
    if (dropdownOpen) {
      searchInputElmRef.current?.focus();
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const doc = documentService() as Document;

    if (dropdownOpen) {
      doc.addEventListener('click', handleClickOutside, false);
    }

    return (): void => {
      doc.removeEventListener('click', handleClickOutside, false);
    };
  }, [documentService, dropdownOpen]);

  useEffect(() => {
    setSelectedItem(preSelectedItem ?? null);
    setSearchText(defaultSearchText);
    setSearchTextChanged(false);
  }, [items]);

  useEffect(() => {
    if (!preSelectedItem) {
      return;
    }
    setSelectedItem(preSelectedItem);
    setSearchText(isStateType(preSelectedItem) ? preSelectedItem.name ?? '' : preSelectedItem.Country ?? '');
  }, [preSelectedItem]);

  return (
    <div className="dealer-locator-landing-search__dropdown" ref={dropdownElmRef}>
      <div
        className={`dealer-locator-landing-search__dropdown-input ${
          dropdownOpen ? 'dealer-locator-landing-search__dropdown-input--open' : ''
        } `}
        onClick={() => handleInputClick()}
      >
        <span
          className={`dealer-locator-landing-search__dropdown-label ${
            dropdownOpen || selectedItem ? '' : 'dealer-locator-landing-search__dropdown-label--center'
          }`}
        >
          {label}
        </span>
        {dropdownOpen ? (
          <input
            className="dealer-locator-landing-search__dropdown-input-field"
            type="text"
            value={searchText}
            ref={searchInputElmRef}
            onChange={handleInputChange}
            onKeyDown={keyDownHandler}
            autoComplete="off"
          />
        ) : (
          <span className="dealer-locator-landing-search__dropdown-input-text">{getOptionText(selectedItem)}</span>
        )}
        <Icon iconName={arrowIcon} extraClassName="dealer-locator-landing-search__dropdown-input-icon" size={iconSize['dsk']} />
      </div>
      {dropdownOpen && (
        <ul
          className="dealer-locator-landing-search__dropdown-list"
          onMouseLeave={() => {
            setHighligtedItemIndex(null);
            setDropdownHover(false);
          }}
        >
          {visibleItems?.map((item, i) => (
            <li
              className={`dealer-locator-landing-search__dropdown-list-item ${
                highligtedItemIndex === i ? 'dealer-locator-landing-search__dropdown-list-item--highligted' : ''
              }`}
              onMouseEnter={() => {
                setHighligtedItemIndex(i);
                setDropdownHover(true);
              }}
              onClick={() => handleListItemSelection(item)}
              key={getOptionKey(item)}
            >
              <HiglightedOption keyword={searchText} optionText={getOptionText(item)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
